import { storeToRefs } from 'pinia'
import { sum, multiply } from '@ms/utils-es/math'
import { useGlobalConfigStore } from '@ms/stores/globalConfig'
import { useBetslipStore } from '@ms/stores/betslip'
import { isBetBuilderSelection } from '@ms/stores/betslip/func'
import {
  whenTax,
  getToReturnPlanB,
  getFlexibleTaxInfo,
  getSuppleTaxInfoFirst,
  getSuppleTaxInfoSecond,
  getStandardSingleTaxInfo,
  getStandardMultipleTaxInfo,
  calcAllMultiplesTaxInfo,
  calcStandardSingleTaxInfo,
  calcStandardMultipleTaxInfo,
  calcSingleStakeTaxInfo,
  getTaxInfo
} from '@ms/stores/betslip/calc'
import { MAX_BETSLIP_SELECTIONS_SIZE } from '@ms/setting/betslip'
import { useBonusStore } from '@ms/stores/betslip/useBonus.js'
import { useBankerStore } from '@ms/stores/betslip/useBanker.js'
import { useSystemBetStore } from '@ms/stores/betslip/useSystemBet.js'
import { getEventCountMap, getBetCount, getMultipleName, getTotalOddsRange, getMaxOddsForBets } from '@ms/components/betslip/calculate.js'
import colors from '@ms/components/betslip/colors.js'
import { getActualPayAmount } from '@ms/components/betslip/voucher.js'
import { useBetVouchersStore } from '@ms/stores/betVouchers.js'
import { getOrderType } from '@ms/stores/betslip/parser.js'

export default {
  selectionNum: (state) => state.selectionList.length,
  betBuilderSize() {
    return this.selectionList.filter(isBetBuilderSelection).length
  },
  disabledOfBetBuilder() {
    const { betBuilderSize } = this

    if (!useGlobalConfigStore().isBetBuilderCanMixBet) {
      // 两个 bet builder 混合投注
      if (betBuilderSize > 1) return true
      return betBuilderSize === 1 && betBuilderSize < this.selectionNum
    }

    return false
  },
  // 当前处于Multiple+模式下（即n＜N)
  isMultiplePlusMode() {
    const { magicNumber, isAllMultiplesMode } = useBetslipStore()
    return !isAllMultiplesMode && magicNumber && magicNumber < this.selectionNum
  },
  // 投注类型 single, multiple, system, flexible, supple
  orderType() {
    return getOrderType(this.multipleLength, this.isMultiplePlusMode)
  },
  // 对应 event 计数，如 { [eventId]: [count] }
  eventCountMap: (state) => {
    const eventIds = state.selectionList.map((x) => x.eventId)
    return getEventCountMap(eventIds)
  },

  // 重复的 event id list
  duplicateEventIds() {
    return Object.entries(this.eventCountMap)
      .filter(([, count]) => count > 1)
      .map(([key]) => key)
  },
  // 重复 event 颜色
  dupliEventColors() {
    const res = {}
    this.duplicateEventIds.forEach((id, index) => {
      res[id] = colors[index]
    })
    return res
  },

  // sub bet 计数
  betCount() {
    return getBetCount(this.selectionList, this.multipleLength)
  },

  // event 不重复的 selection 种类计数
  multipleLength() {
    return Object.keys(this.eventCountMap).length
  },

  // 串关方式：以去重后的event数量作为betslip显示最高串关名称
  multipleName() {
    const unionSelectionLen = this.multipleLength
    return getMultipleName(unionSelectionLen)
  },

  totalStake() {
    const { isAllMultiplesMode, singleStakeList, standardMultipleStake } = useBetslipStore()
    // 快速投注
    if (this.selectionNum === 1) {
      return this.fastStake
    }

    if (isAllMultiplesMode) {
      return useSystemBetStore().systemTotalStake
    } else {
      let singleStake = 0
      for (const bet of singleStakeList) {
        singleStake = sum(singleStake, bet.stake)
      }
      const { stake, betCount, count } = standardMultipleStake
      if (stake && count !== 1) {
        return sum(singleStake, multiply(stake, betCount))
      } else {
        return singleStake
      }
    }
  },

  // 最高串关 folds 总赔率, return array
  topMutipleTotalOdds() {
    // singles
    if (this.multipleLength === 1) {
      const oddsListMap = {}
      const { eventCountMap } = this
      this.selectionList.forEach((x) => {
        const odds = +x.outcomeInfo.odds
        if (eventCountMap[x.eventId] > 1) {
          // 相同比赛，互斥
          if (oddsListMap[x.eventId]) {
            oddsListMap[x.eventId].push(odds)
          } else {
            oddsListMap[x.eventId] = [odds]
          }
        } else {
          oddsListMap[x.eventId] = odds
        }
      })
      let min
      let max = 0
      Object.values(oddsListMap).forEach((x) => {
        if (Array.isArray(x)) {
          // return Math.min(...x)
          const minx = Math.min(...x)
          const maxx = Math.max(...x)
          if (!min || min > minx) {
            min = minx
          }
          max += maxx
        } else {
          if (!min || min > x) {
            min = x
          }
          max += x
        }
      })
      return [min, max]
    }

    const { maxLimitOdds } = storeToRefs(useGlobalConfigStore())
    const [minTotalOdds, maxTotalOdds] = getTotalOddsRange(this.selectionList, this.multipleLength, maxLimitOdds.value)

    if (minTotalOdds.toFixed(4) === maxTotalOdds.toFixed(4)) {
      const val = parseFloat(minTotalOdds.toFixed(4))
      return [val, val]
    }

    return [minTotalOdds, maxTotalOdds]
  },
  betType() {
    const { bankerMap } = useBankerStore()
    return this.multipleLength - Object.keys(bankerMap).length
  },
  restSelectionList(state) {
    const { bankerMap } = useBankerStore()
    return state.selectionList.filter((x) => !bankerMap[x.id])
  },
  maxBetOdds() {
    const { bankerTotalOdds } = useBankerStore()
    let max = getMaxOddsForBets(this.restSelectionList, this.betType)
    const { maxLimitOdds } = storeToRefs(useGlobalConfigStore())
    if (bankerTotalOdds) {
      max *= bankerTotalOdds
    }
    if (max > maxLimitOdds.value) {
      max = maxLimitOdds.value
    }
    if (max) {
      max = Number(max.toFixed(4))
    }
    return max
  },
  // 当前有效events数量
  validBonusEventsLength() {
    const { selectionList } = this
    const { bonusRatios, qualifyingOddsLimit } = useBonusStore()

    if (!bonusRatios || Object.keys(bonusRatios).length < 1 || this.selectionNum > MAX_BETSLIP_SELECTIONS_SIZE) return

    // 同一比赛的不同 outcome 只能算作一场比赛，所以这里需要对 eventId 进行去重
    const uniqueEventIds = new Set()
    const validBonusEvents = selectionList.filter((selection) => {
      const { outcomeInfo, eventId } = selection

      if (uniqueEventIds.has(eventId)) {
        return false
      }

      const odds = parseFloat(outcomeInfo.odds)

      if (odds >= qualifyingOddsLimit) {
        uniqueEventIds.add(eventId)
        return true
      }
      return false
    })

    return validBonusEvents.length
  },
  currentBonusRatio() {
    const num = this.validBonusEventsLength
    const { bonusRatios } = useBonusStore()
    const val = bonusRatios[num]
    if (!val) return ''
    return val + '%'
  },
  initialFastToReturn() {
    const stake = +this.fastStake
    if (this.selectionNum !== 1 || !stake) return
    const { potWinMax } = useGlobalConfigStore()
    const result = stake * this.selectionList[0].outcomeInfo.odds
    return result > potWinMax ? potWinMax : result
  },
  stakeRatio() {
    const { totalStake } = this
    if (!totalStake) return 1
    const { selectedVoucher } = useBetVouchersStore()
    const actualPayAmount = getActualPayAmount(selectedVoucher, totalStake)
    return +actualPayAmount / +totalStake
  },
  standardSingleTaxInfo() {
    const { singleStakeList } = useBetslipStore()
    const { selectionNum, stakeRatio } = this
    return getStandardSingleTaxInfo({ selectionNum, stakeRatio, singleStakeList })
  },
  standardMultipleTaxInfo() {
    const { standardSingleTaxInfo, stakeRatio } = this
    return getStandardMultipleTaxInfo({ standardSingleTaxInfo, stakeRatio })
  },
  flexibleTaxInfo() {
    const { orderType, totalStake } = this
    return getFlexibleTaxInfo(orderType, totalStake)
  },
  toReturnPlanB() {
    const { orderType, totalStake } = this
    return getToReturnPlanB(orderType, totalStake)
  },
  suppleTaxInfoFirst() {
    const { orderType, totalStake, toReturnPlanB } = this
    return getSuppleTaxInfoFirst(orderType, totalStake, toReturnPlanB?.prize1)
  },
  suppleTaxInfoSecond() {
    const { orderType, totalStake, toReturnPlanB } = this
    return getSuppleTaxInfoSecond(orderType, totalStake, toReturnPlanB?.prize2)
  },
  taxInfo() {
    const { selectionNum, totalStake } = this
    if (selectionNum === 0) return
    const { potWinMax, calcTaxInfo } = useGlobalConfigStore()
    const { singleStakeList, standardMultipleStake } = useBetslipStore()
    const { stakeRatio, orderType } = this
    if (selectionNum === 1) {
      const { fastStake, initialFastToReturn } = this
      if (!fastStake) {
        return {
          toReturn: 0
        }
      }
      const initialToReturn = initialFastToReturn <= potWinMax ? initialFastToReturn : potWinMax
      return whenTax(() => {
        const { winningTax, extraBonus } = calcTaxInfo(initialToReturn, fastStake, stakeRatio)
        return {
          initialToReturn,
          winningTax,
          extraBonus,
          toReturn: initialToReturn - winningTax + extraBonus
        }
      }, initialToReturn)
    }

    const initialToReturn = [0, 0]
    const bonus = [0, 0]
    const winningTax = [0, 0]
    const extraBonus = [0, 0]
    const { isAllMultiplesMode } = useBetslipStore()
    if (isAllMultiplesMode) {
      const { selectionList } = this
      calcAllMultiplesTaxInfo({ initialToReturn, winningTax, extraBonus, stakeRatio, selectionList })
    } else {
      // standard
      if (orderType === 'single') {
        const { standardSingleTaxInfo } = this
        calcStandardSingleTaxInfo({ initialToReturn, winningTax, extraBonus, standardSingleTaxInfo })
      } else if (orderType === 'supple') {
        return this.suppleTaxInfoFirst
      } else {
        // multiple
        const { stake } = standardMultipleStake
        if (stake) {
          const { standardMultipleTaxInfo } = this
          calcStandardMultipleTaxInfo({ orderType, initialToReturn, bonus, winningTax, extraBonus, standardMultipleTaxInfo })
        }

        // single
        if (singleStakeList.length) {
          const { standardSingleTaxInfo } = this
          calcSingleStakeTaxInfo({ initialToReturn, winningTax, extraBonus, standardSingleTaxInfo })
        }
      }
    }

    return getTaxInfo({ isAllMultiplesMode, initialToReturn, bonus, winningTax, extraBonus, stakeRatio, totalStake, orderType })
  }
}
